import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles, Trophy, RefreshCw, Check, X as XIcon } from "lucide-react";
import confetti from "canvas-confetti";
import { quiz } from "@/data/edu";
import axios from "axios";

import { API } from "@/lib/api";

export default function QuizPage() {
  const [step, setStep] = useState(0); // -1 = intro, 0..N = questions, N+1 = finish
  const [answers, setAnswers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showHint, setShowHint] = useState(false);

  const finished = step >= quiz.length;
  const current = quiz[step];

  const score = useMemo(
    () => answers.reduce((s, a, i) => s + (a === quiz[i].correct ? 1 : 0), 0),
    [answers],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    setSelected(null);
    setShowHint(false);
  }, [step]);

  useEffect(() => {
    if (finished && score / quiz.length >= 0.7) {
      confetti({
        particleCount: 140,
        spread: 90,
        startVelocity: 40,
        origin: { y: 0.6 },
        colors: ["#84C5F4", "#FDE68A", "#FECACA", "#DDD6FE"],
      });
      axios.post(`${API}/quiz/score`, { score, total: quiz.length }).catch(() => {});
    }
  }, [finished, score]);

  const onAnswer = (idx) => {
    if (selected !== null) return;
    setSelected(idx);
    setTimeout(() => {
      setAnswers((prev) => [...prev, idx]);
      setStep((s) => s + 1);
    }, 900);
  };

  const reset = () => {
    setAnswers([]);
    setStep(0);
    setSelected(null);
  };

  return (
    <div className="pt-32 md:pt-40 pb-24 min-h-screen relative overflow-hidden" data-testid="quiz-page">
      {/* Aurora */}
      <div className="aurora" style={{ width: 480, height: 480, background: "#FDE68A", top: "10%", left: "-10%" }} />
      <div className="aurora" style={{ width: 420, height: 420, background: "#DDD6FE", bottom: "0%", right: "-10%" }} />

      <div className="max-w-3xl mx-auto px-6 md:px-10 relative">
        <Link
          to="/"
          data-testid="quiz-back"
          className="inline-flex items-center gap-2 chip !bg-white/80 !border-white"
        >
          <ArrowLeft size={14} /> Strona główna
        </Link>

        <div className="mt-6 flex items-center justify-between">
          <span className="chip"><Sparkles size={12} /> Quiz</span>
          {!finished && (
            <span className="text-xs uppercase tracking-[0.18em] font-bold text-[#0b2a55]/60" data-testid="quiz-progress">
              Pytanie {step + 1} / {quiz.length}
            </span>
          )}
        </div>

        {/* Progress bar */}
        <div className="mt-3 h-1.5 rounded-full bg-white overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${((finished ? quiz.length : step) / quiz.length) * 100}%` }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="h-full bg-gradient-to-r from-[#84C5F4] via-[#60A5FA] to-[#FDE68A]"
          />
        </div>

        <AnimatePresence mode="wait">
          {!finished && (
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -30 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="mt-10"
            >
              <h1
                className="font-display font-semibold text-[#051937] text-3xl sm:text-4xl md:text-5xl leading-[1.05] tracking-tight"
                data-testid="quiz-question"
              >
                {current.q}
              </h1>

              <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {current.options.map((opt, i) => {
                  const isCorrect = selected !== null && i === current.correct;
                  const isWrongPick = selected === i && i !== current.correct;
                  return (
                    <motion.li key={opt} whileTap={{ scale: 0.98 }}>
                      <button
                        onClick={() => onAnswer(i)}
                        disabled={selected !== null}
                        data-testid={`quiz-option-${i}`}
                        className={`w-full text-left rounded-2xl p-5 border-2 transition-all font-display text-lg leading-tight ${
                          selected === null
                            ? "bg-white border-[#BAE6FD] hover:border-[#60A5FA] hover:-translate-y-1 shadow-[0_8px_20px_-10px_rgba(11,42,85,0.15)]"
                            : isCorrect
                              ? "bg-[#DCFCE7] border-[#10B981]"
                              : isWrongPick
                                ? "bg-[#FEE2E2] border-[#EF4444]"
                                : "bg-white border-[#BAE6FD] opacity-60"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="font-semibold text-[#051937]">{opt}</span>
                          {isCorrect && <Check className="text-[#10B981]" size={20} />}
                          {isWrongPick && <XIcon className="text-[#EF4444]" size={20} />}
                        </div>
                      </button>
                    </motion.li>
                  );
                })}
              </ul>

              <div className="mt-6 flex items-center justify-between">
                <button
                  onClick={() => setShowHint((v) => !v)}
                  data-testid="quiz-hint"
                  className="text-sm font-semibold text-[#0b2a55]/70 hover:text-[#051937] animated-underline"
                >
                  {showHint ? "Ukryj podpowiedź" : "Podpowiedź od Duszka ✨"}
                </button>
                <span className="text-xs uppercase tracking-[0.18em] font-bold text-[#0b2a55]/50">
                  wynik: {score}
                </span>
              </div>
              <AnimatePresence>
                {showHint && (
                  <motion.p
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    className="mt-3 font-script text-[#60A5FA] text-xl"
                  >
                    „{current.hint}”
                  </motion.p>
                )}
              </AnimatePresence>
            </motion.div>
          )}

          {finished && (
            <motion.div
              key="done"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="mt-12 rounded-[2rem] bg-white border border-[#BAE6FD] p-8 md:p-14 text-center relative overflow-hidden"
              data-testid="quiz-result"
            >
              <motion.div
                animate={{ y: [-6, 6, -6], rotate: [-4, 4, -4] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="w-20 h-20 mx-auto rounded-full bg-[#FDE68A] grid place-items-center text-[#051937]"
              >
                <Trophy size={36} />
              </motion.div>
              <h2 className="mt-6 font-display font-semibold text-[#051937] text-4xl md:text-5xl leading-tight">
                {score >= 6 ? "Prawdziwy przyjaciel Tonka!" : score >= 3 ? "Świetnie idziesz!" : "Zajrzyj jeszcze raz do bajek."}
              </h2>
              <p className="mt-4 text-lg text-[#0b2a55]/80">
                Zdobyłeś{" "}
                <span className="font-display font-semibold text-[#60A5FA]" data-testid="quiz-score">
                  {score} / {quiz.length}
                </span>{" "}
                punktów.
              </p>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                <button
                  onClick={reset}
                  data-testid="quiz-retry"
                  className="inline-flex items-center gap-2 rounded-full bg-[#051937] hover:bg-[#0b2a55] text-white px-6 py-3 font-semibold"
                >
                  <RefreshCw size={16} /> Spróbuj jeszcze raz
                </button>
                <Link
                  to="/sklep"
                  className="inline-flex items-center gap-2 rounded-full bg-[#FDE68A] hover:bg-[#FCD34D] text-[#051937] px-6 py-3 font-semibold"
                >
                  Zobacz książki <ArrowRight size={16} />
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
